import { useEffect } from 'react'
import type { ExploreProperty } from '../data'
import type { CardCompare } from '../../../common/property-card/card-compare'
import { useCompare } from './CompareProvider'
import type { QuoteForCompare } from './model'

/**
 * The grid's and the slider's side of Compare Homes (#1713): report the rows
 * they render (and any full-price quote that landed) after every render, and
 * get back the per-card props. Returns `cardProps` that is always callable and
 * yields undefined while compare is off, so a card renders exactly as before.
 *
 * The report carries rows the explorer already loaded: it is how the compare
 * table gets its facts without asking the API for anything.
 */
export function useCompareResults(
  properties: ExploreProperty[],
  quoteCache: Record<string, QuoteForCompare | 'loading' | 'failed'>,
  settled: boolean,
): (property: ExploreProperty, quote?: QuoteForCompare) => CardCompare | undefined {
  const api = useCompare()
  const ingest = api?.ingest
  // No dependency list on purpose: `properties` and `quoteCache` are rebuilt every render and
  // ingest() is cheap and writes state only when a count actually changed.
  useEffect(() => {
    if (!ingest) return
    const quotes: Record<string, QuoteForCompare> = {}
    for (const [id, quote] of Object.entries(quoteCache)) {
      if (quote !== 'loading' && quote !== 'failed') quotes[id] = quote
    }
    ingest(properties, quotes, settled)
  })
  return api ? api.cardProps : noCompare
}

function noCompare(): undefined {
  return undefined
}
